import { ImageMetadata } from '@lukso/lsp4-contracts'

import useErc725 from '@/popup/compositions/useErc725'
import {
  getImageUrlBySize,
  fetchAndConvertImage,
} from '@/popup/utils/getProfileImages'
import TokenDefault from '@/assets/token-default.svg'
import { PopupDatabase } from '@/popup/storage/Database'
import { DigitalAsset } from '@/shared/models/Profile'

const TOKEN_ICON_MIN_WIDTH = 88
const TOKEN_ICON_MAX_HEIGHT = 200
const TOKEN_IMAGE_MIN_WIDTH = 56
const TOKEN_IMAGE_MAX_HEIGHT = 200

/**
 * gets cached asset details or fetches them from the blockchain and saves to cache
 *
 * @param assetAddress - asset token address
 * @returns asset details
 */
export const getAssetDetails = async (
  assetAddress: string
): Promise<DigitalAsset | undefined> => {
  const { fetchDigitalAsset } = useErc725()
  let asset = await PopupDatabase.getDigitalAsset(assetAddress)
  if (!asset || !asset.icon) {
    const { name, symbol, metaData } = await fetchDigitalAsset(assetAddress)
    const { icon, images } = await getTokenImages(
      metaData?.icon,
      metaData?.images
    )
    asset = {
      assetAddress,
      icon: icon === TokenDefault ? undefined : icon,
      name,
      symbol,
      images,
      links: metaData?.links,
      description: metaData?.description,
    }
    await PopupDatabase.put('digitalAssets', asset)
  }
  return asset
}

/**
 * Select the correct size for token icon and images
 *
 * @param metaDataIcon - icon metadata array
 * @param metaDataImages - images metadata array
 * @returns token icon and images
 */
export const getTokenImages = async (
  metaDataIcon?: ImageMetadata[],
  metaDataImages?: ImageMetadata[][]
): Promise<{ icon: string; images: string[] }> => {
  let icon = TokenDefault
  const images = []

  if (metaDataIcon && metaDataIcon.length > 0) {
    const foundImage = getImageUrlBySize(
      metaDataIcon,
      TOKEN_ICON_MIN_WIDTH,
      TOKEN_ICON_MAX_HEIGHT
    )

    if (foundImage) {
      icon = await fetchAndConvertImage(foundImage)
    }
  }

  if (metaDataImages && metaDataImages.length > 0) {
    for await (const image of metaDataImages) {
      const foundImage = getImageUrlBySize(
        image,
        TOKEN_IMAGE_MIN_WIDTH,
        TOKEN_IMAGE_MAX_HEIGHT
      )

      if (foundImage) {
        images.push(await fetchAndConvertImage(foundImage))
      } else {
        images.push(TokenDefault)
      }
    }
  }

  return {
    icon,
    images,
  }
}
